import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff, KeyRound, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { CHANGE_PASSWORD_MUTATION } from "@/graphql/auth";

type ChangePasswordFormData = {
  newPassword: string;
  confirmPassword: string;
};

interface ChangePasswordDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function ChangePasswordDialog({ isOpen, onClose, onSuccess }: ChangePasswordDialogProps) {
  const { t } = useTranslation("settings");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Create schema with translations
  const changePasswordSchemaWithTranslations = z
    .object({
      newPassword: z.string().min(6, t("profile.changePassword.validation.minLength")),
      confirmPassword: z.string(),
    })
    .superRefine((data, ctx) => {
      if (data.newPassword !== data.confirmPassword) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("profile.changePassword.validation.passwordMismatch"),
          path: ["confirmPassword"],
        });
      }
    });

  // Form setup
  const form = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchemaWithTranslations),
    mode: "onChange",
    criteriaMode: "all",
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  });

  // Change password mutation
  const [changePassword, { loading }] = useMutation(CHANGE_PASSWORD_MUTATION, {
    onCompleted: () => {
      toast.success(t("profile.changePassword.success"));
      handleClose();
      onSuccess?.();
    },
    onError: (error) => {
      console.error("Change password error:", error);
      toast.error(error.message || t("profile.changePassword.error"));
    },
  });

  // Handle form submission
  const onSubmit = async (data: ChangePasswordFormData) => {
    try {
      await changePassword({
        variables: {
          input: {
            newPassword: data.newPassword,
          },
        },
      });
    } catch (error) {
      console.error("Change password error:", error);
    }
  };

  // Handle dialog close
  const handleClose = () => {
    form.reset();
    setShowPassword(false);
    setShowConfirmPassword(false);
    onClose();
  };

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      form.reset();
      setShowPassword(false);
      setShowConfirmPassword(false);
    }
  }, [isOpen, form]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg" onCloseAutoFocus={() => form.reset()}>
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <KeyRound className="h-5 w-5" />
            <span>{t("profile.changePassword.title")}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="text-sm text-muted-foreground">
          {t("profile.changePassword.passwordRequirements")}
        </div>

        <div>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {/* New Password Field */}
              <FormField
                control={form.control}
                name="newPassword"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>{t("profile.changePassword.newPassword")}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          {...field}
                          type={showPassword ? "text" : "password"}
                          placeholder={t("profile.changePassword.newPasswordPlaceholder")}
                          disabled={loading}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowPassword(!showPassword)}
                          disabled={loading}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage>{fieldState.error?.message}</FormMessage>
                  </FormItem>
                )}
              />

              {/* Confirm Password Field */}
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>{t("profile.changePassword.confirmPassword")}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          {...field}
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder={t("profile.changePassword.confirmPasswordPlaceholder")}
                          disabled={loading}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          disabled={loading}
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage>{fieldState.error?.message}</FormMessage>
                  </FormItem>
                )}
              />

              <div className="flex justify-end space-x-2 pt-4">
                <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
                  {t("common.cancel")}
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {t("profile.changePassword.submit")}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
